import { useState, useEffect, useMemo } from "react";
import { RouterShell, ProfilePage, usePageTracking, useMyDailyDirectClient } from "@mydailydirect/app-kit";
import { formatPaise, useToast, type NavItem } from "@mydailydirect/ui";
import { ClipboardList, ShoppingCart, Store, User, Sparkles } from "lucide-react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CartProvider, useCart } from "./lib/cart";
import { useRetailerId } from "./lib/useRetailerId";
import { CartDrawer } from "./components/CartDrawer";
import { resolvePack, packView } from "@mydailydirect/ui";
import { BrowsePage } from "./pages/BrowsePage";
import { CuratedForYouPage } from "./pages/CuratedForYouPage";
import { OrderDetailPage } from "./pages/OrderDetailPage";
import { OrdersPage } from "./pages/OrdersPage";
import { OrderSuccessPage } from "./pages/OrderSuccessPage";
import { ProductDetailPage } from "./pages/ProductDetailPage";
import { DesignLabPage } from "./pages/DesignLabPage";

export function App() {
  return (
    <CartProvider>
      <CatalogShell />
    </CartProvider>
  );
}

function CatalogShell() {
  usePageTracking("catalog");
  const cart = useCart();
  const client = useMyDailyDirectClient();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { retailer, isVerified } = useRetailerId();

  const [isCartOpen, setIsCartOpen] = useState(false);

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  // Listen for global open-cart event from anywhere in the application
  useEffect(() => {
    const handleOpenCart = () => setIsCartOpen(true);
    window.addEventListener("mdd:open-cart", handleOpenCart);
    return () => window.removeEventListener("mdd:open-cart", handleOpenCart);
  }, []);

  // Fetch skus to calculate live cart total paise
  const skusQuery = useQuery({ queryKey: ["skus"], queryFn: () => client.skus.list() });

  const cartTotal = useMemo(() => {
    const byId = new Map((skusQuery.data ?? []).map((s) => [s.id, s]));
    return cart.lines.reduce((sum, l) => {
      const sku = byId.get(l.skuId);
      if (!sku) return sum;
      const pack = resolvePack(sku, l.packId);
      const price = packView(sku, pack).pricePaise;
      return sum + price * l.qty;
    }, 0);
  }, [cart.lines, skusQuery.data]);

  // Order placement mutation for Cart Drawer checkout
  const placeOrder = useMutation({
    mutationFn: (paymentMethod: string) => {
      const retailerId = retailer?.id ?? null;
      if (retailerId === null) throw new Error("Select a retailer first.");
      if (!isVerified) throw new Error("Shop verification required before placing orders. Please upload your documents in Profile.");
      return client.orders.create({
        retailerId,
        lines: cart.lines.map((l) => ({ skuId: l.skuId, qty: l.qty, packId: l.packId ?? null })),
        paymentMethod,
      } as any);
    },
    onSuccess: (order) => {
      cart.clear();
      setIsCartOpen(false);
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["skus"] });
      toast({
        title: `Order #${order.id} Confirmed!`,
        description: `Total ${formatPaise(order.totalPaise)} — scheduled for morning slot.`,
        variant: "success",
      });
      navigate(`/order-success/${order.id}`, { state: { order } });
    },
    onError: (err) => {
      toast({
        title: "Could not place order",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "error",
      });
    },
  });

  const nav: NavItem[] = [
    { label: "Catalog", href: "/", icon: Store },
    { label: "Curated For You", href: "/for-you", icon: Sparkles },
    { label: "Cart", href: "", icon: ShoppingCart, badge: cart.totalItems, onClick: openCart },
    { label: "My Orders", href: "/orders", icon: ClipboardList },
    { label: "Profile", href: "/profile", icon: User },
  ];

  return (
    <>
      <RouterShell
        appName="My Daily Direct"
        subtitle="Retailer Store"
        nav={nav}
        bottomNav={nav}
        cartBadgeCount={cart.totalItems}
        onCartClick={openCart}
      >
        <Routes>
          <Route path="/" element={<BrowsePage />} />
          <Route path="/for-you" element={<CuratedForYouPage />} />
          <Route path="/lab" element={<DesignLabPage />} />
          <Route path="/__lab" element={<DesignLabPage />} />
          <Route path="/product/:id" element={<ProductDetailPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/order-success/:id" element={<OrderSuccessPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:id" element={<OrderDetailPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </RouterShell>

      {/* Global Cart Drawer: renders via createPortal at root on top of any screen */}
      <CartDrawer
        isOpen={isCartOpen}
        onClose={closeCart}
        cartTotal={cartTotal}
        onPlaceOrder={(method) => placeOrder.mutate(method)}
        isPlacingOrder={placeOrder.isPending}
      />
    </>
  );
}
